import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Typography, Stack, FormControl, InputLabel, Select, MenuItem, Button, TablePagination, TextField, TableSortLabel } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { SCADAAlarm } from '../../types';
import { scadaAlarmsAPI } from '../../services/alarmService';
import { useSiteContext } from '../../hooks/useSiteContext';

export const AlarmHistory: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [alarms, setAlarms] = useState<SCADAAlarm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [priority, setPriority] = useState('');
  const [alarmType, setAlarmType] = useState('');
  const [dateFromStr, setDateFromStr] = useState('');
  const [dateToStr, setDateToStr] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [orderBy, setOrderBy] = useState<string>('-triggered_at');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');

  const distinctTypes = useMemo(() => Array.from(new Set(alarms.map(a => a.alarm_type))).sort(), [alarms]);

  const fetchHistory = async () => {
    try {
      const params: any = { ordering: orderBy, page: page + 1, page_size: rowsPerPage };
      if (currentSite?.siteId) params.site_id = currentSite.siteId;
      if (priority) params.priority = priority;
      if (alarmType) params.alarm_type = alarmType;
      if (dateFromStr) params.date_from = new Date(dateFromStr).toISOString();
      if (dateToStr) params.date_to = new Date(dateToStr).toISOString();
      if (tagSearch) params.search = tagSearch;
      const data = await scadaAlarmsAPI.getAll(params) as any;
      setAlarms(data.results || []);
      setTotalCount(data.count || (data.results ? data.results.length : 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alarm history');
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSite?.siteId, page, rowsPerPage, orderBy]);

  const handleSort = (field: string) => {
    const isAsc = orderBy === field && orderDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    const newOrderBy = newDirection === 'desc' ? `-${field}` : field;
    setOrderBy(newOrderBy);
    setOrderDirection(newDirection);
    setPage(0); // Reset to first page when sorting
  };

  const applyFilters = () => { setPage(0); fetchHistory(); };
  const clearFilters = () => {
    setPriority(''); setAlarmType(''); setDateFromStr(''); setDateToStr(''); setTagSearch(''); setPage(0); fetchHistory();
  };

  const renderTagName = (a: any) => {
    if (a.tag_name) return a.tag_name;
    if (a.tag && typeof a.tag === 'object' && a.tag.tag_name) return a.tag.tag_name;
    return '';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Alarm History</Typography>
      {error && <Typography color="error">{error}</Typography>}
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Search by Tag"
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            placeholder="Type to search tags..."
            sx={{ minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={priority} label="Priority" onChange={e => setPriority(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Alarm Type</InputLabel>
            <Select value={alarmType} label="Alarm Type" onChange={e => setAlarmType(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {distinctTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="From"
            type="datetime-local"
            value={dateFromStr}
            onChange={(e) => setDateFromStr(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="datetime-local"
            value={dateToStr}
            onChange={(e) => setDateToStr(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={applyFilters}>Apply</Button>
            <Button variant="text" onClick={clearFilters}>Clear</Button>
          </Stack>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy.includes('tag__tag_name')}
                  direction={orderBy === 'tag__tag_name' ? 'asc' : 'desc'}
                  onClick={() => handleSort('tag__tag_name')}
                >
                  Tag
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy.includes('triggered_at')}
                  direction={orderBy === 'triggered_at' ? 'asc' : 'desc'}
                  onClick={() => handleSort('triggered_at')}
                >
                  Triggered
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy.includes('acknowledged_at')}
                  direction={orderBy === 'acknowledged_at' ? 'asc' : 'desc'}
                  onClick={() => handleSort('acknowledged_at')}
                >
                  Acknowledged
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy.includes('cleared_at')}
                  direction={orderBy === 'cleared_at' ? 'asc' : 'desc'}
                  onClick={() => handleSort('cleared_at')}
                >
                  Cleared
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy.includes('alarm_type')}
                  direction={orderBy === 'alarm_type' ? 'asc' : 'desc'}
                  onClick={() => handleSort('alarm_type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy.includes('priority')}
                  direction={orderBy === 'priority' ? 'asc' : 'desc'}
                  onClick={() => handleSort('priority')}
                >
                  Priority
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy.includes('state')}
                  direction={orderBy === 'state' ? 'asc' : 'desc'}
                  onClick={() => handleSort('state')}
                >
                  State
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alarms.map(a => (
              <TableRow key={a.id}>
                <TableCell>{renderTagName(a)}</TableCell>
                <TableCell>{a.triggered_at ? format(parseISO(a.triggered_at), 'MMM dd, HH:mm:ss') : ''}</TableCell>
                <TableCell>{a.acknowledged_at ? format(parseISO(a.acknowledged_at), 'MMM dd, HH:mm:ss') : ''}</TableCell>
                <TableCell>{a.cleared_at ? format(parseISO(a.cleared_at), 'MMM dd, HH:mm:ss') : ''}</TableCell>
                <TableCell>{a.alarm_type}</TableCell>
                <TableCell>{a.priority}</TableCell>
                <TableCell>{a.state}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={totalCount} page={page} onPageChange={(_,p)=>setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e)=>{setRowsPerPage(parseInt(e.target.value,10)); setPage(0);}} />
      </TableContainer>
    </Box>
  );
};


